// components/organisation/memberContextActions.tsx
import { ChartLine, Circle, Crown, Download, KeyRound, Mail, Shield, UserCheck, Users, X } from '@/components/ui/lucide-memo';
import type { MemberData, QuickMenuAction } from '#types/types';

interface OrganisationMemberContextActionParams {
  currentOrgId?: string | null;
  isFaterAdmin: boolean;
  canDelete: boolean;
  canManageSubordinates: boolean;
  canChangeEmail: (member: MemberData) => boolean;
  canDeleteMember: (member: MemberData) => boolean;
  isCurrentUser: (member: MemberData) => boolean;
  togglingAdminId?: string | null;
  changingEmailUserId?: string | null;
  sendingTeamReportUserId?: string | null;
  onImpersonateUser: (userId: string, userName: string) => void;
  onResetPassword: (member: MemberData) => void;
  onEditPermissions: (member: MemberData) => void;
  onDownloadAvatar: (member: MemberData) => void;
  onDeleteAvatar: (member: MemberData) => void;
  onOpenSubordinateSelector: (member: MemberData) => void;
  onSendTeamReportToSelf: (member: MemberData) => void;
  onToggleAdmin: (member: MemberData) => void;
  onChangeMemberEmail: (member: MemberData) => void;
  onDeleteUser: (member: MemberData) => void;
}

export const buildOrganisationMemberContextActions = (
  member: MemberData,
  params: OrganisationMemberContextActionParams
): QuickMenuAction[] => {
  if (!params.currentOrgId) return [];

  const actions: QuickMenuAction[] = [];

  if (params.isFaterAdmin && member.authId && !member.authId.startsWith('invited_')) {
    actions.push({
      label: 'Impersonate',
      icon: <UserCheck size={14} />,
      onClick: () => params.onImpersonateUser(member.id, member.firstName || member.email),
    });
    actions.push({
      label: 'Reset password…',
      icon: <KeyRound size={14} />,
      onClick: () => params.onResetPassword(member),
    });
    actions.push({
      label: 'Edit permissions…',
      icon: <Shield size={14} />,
      onClick: () => params.onEditPermissions(member),
    });
  }

  actions.push({
    label: 'Download avatar',
    icon: <Download size={14} />,
    onClick: () => params.onDownloadAvatar(member),
  });

  if (member.imageUrl || member.authId) {
    actions.push({
      label: 'Delete avatar',
      icon: <X size={14} className="text-[#FF7369]" />,
      onClick: () => params.onDeleteAvatar(member),
    });
  }

  if (params.canManageSubordinates) {
    actions.push({
      label: 'Add subordinates…',
      icon: <Users size={14} />,
      onClick: () => params.onOpenSubordinateSelector(member),
    });
    if (params.isFaterAdmin) {
      actions.push({
        label: params.sendingTeamReportUserId === member.id ? 'Sending team report…' : 'Send team report to me',
        icon: params.sendingTeamReportUserId === member.id ? <Circle size={14} className="animate-spin" /> : <ChartLine size={14} />,
        onClick: () => params.onSendTeamReportToSelf(member),
      });
    }
  }

  if (params.isFaterAdmin && params.canDelete && !params.isCurrentUser(member)) {
    actions.push({
      label: member.isOrganisationAdmin ? 'Revoke admin' : 'Promote to admin',
      icon: params.togglingAdminId === member.id
        ? <Circle size={14} className="animate-spin" />
        : <Crown size={14} className={member.isOrganisationAdmin ? 'text-white/40' : 'text-[#FFDC49]/80'} />,
      onClick: () => params.onToggleAdmin(member),
    });
  }

  if (params.canChangeEmail(member)) {
    actions.push({
      label: params.changingEmailUserId === member.id ? 'Changing email…' : 'Change email…',
      icon: params.changingEmailUserId === member.id ? <Circle size={14} className="animate-spin" /> : <Mail size={14} />,
      onClick: () => params.onChangeMemberEmail(member),
    });
  }

  if (params.canDeleteMember(member)) {
    actions.push({
      label: 'Delete user…',
      onClick: () => params.onDeleteUser(member),
    });
  }

  return actions;
};
